import { api } from "@/lib/api-client";
import type { Role } from "@/features/auth/types";
import type { ApiKey, ApiKeysEndpoints, NewApiKey } from "@/components/ApiKeysPanel";

// Platform admin — superuser-only pages that replace Django admin.
// Every call works across all tenants.

export type Page<T> = {
  results: T[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
};

export type TenantStatus = "active" | "suspended" | "cancelled";

export type AdminTenant = {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  created_at: string;
  users_count: number;
  owner_email: string | null;
  plan_name: string | null;
  credit_balance_millicents: number | null;
};

export type AdminQuota = {
  max_agents: number;
  max_phone_numbers: number;
  max_team_members: number;
  max_knowledge_documents: number;
  included_minutes: number;
  agents_count: number;
  phone_numbers_count: number;
  team_members_count: number;
  knowledge_documents_count: number;
  minutes_used: number;
};

export type QuotaLimits = Pick<
  AdminQuota,
  "max_agents" | "max_phone_numbers" | "max_team_members" | "max_knowledge_documents" | "included_minutes"
>;

export type AdminMember = {
  membership_id: string;
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  is_active: boolean;
  user_active: boolean;
  /** The user's home tenant (the one their account sits in). */
  is_home: boolean;
  created_at: string;
};

export type AdminTenantDetail = AdminTenant & {
  timezone: string;
  locale: string;
  billing_email: string;
  parent: { id: string; name: string } | null;
  updated_at: string;
  subscription_status: string | null;
  quota: AdminQuota | null;
  members: AdminMember[];
  recent_credit: CreditEntry[];
  subscription: AdminSubscription | null;
  plan_options: { id: string; name: string; is_active: boolean }[];
};

export type AdminSubscription = {
  plan_id: string | null;
  status: "trialing" | "active" | "past_due" | "cancelled" | "paused";
  billing_interval: "monthly" | "annual";
  enforce_minute_limit: boolean;
  cancel_at_period_end: boolean;
  auto_topup_enabled: boolean;
  auto_topup_threshold_cents: number;
  current_period_start: string | null;
  current_period_end: string | null;
  card_brand: string;
  card_last4: string;
  stripe_customer_id: string;
};

export type CreditEntry = {
  id: string;
  kind: "topup" | "usage" | "adjustment";
  amount_millicents: number;
  balance_after_millicents: number;
  description: string;
  reference: string;
  created_at: string;
};

export type AdminUser = {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  tenant: { id: string; name: string } | null;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  email_verified: boolean;
  email_verified_at: string | null;
  failed_login_attempts: number;
  locked: boolean;
  locked_until: string | null;
  last_login_at: string | null;
  mfa_enabled: boolean;
  created_at: string;
};

export type AdminMembership = {
  id: string;
  tenant: { id: string; name: string };
  role: Role;
  is_active: boolean;
  is_home: boolean;
  created_at: string;
};

export type AdminUserDetail = AdminUser & { memberships: AdminMembership[] };

export type PlatformSettings = {
  public_signup_enabled: boolean;
  low_credit_alert_cents: number;
  default_pipeline_version: string;
  multi_workspace_enabled: boolean;
  block_calls_when_suspended: boolean;
  block_agent_management_when_suspended: boolean;
  block_phone_numbers_when_suspended: boolean;
  block_webhooks_when_suspended: boolean;
  block_post_call_processing_when_suspended: boolean;
  updated_at: string;
};

export type UserFilter = "superuser" | "staff" | "inactive" | "locked" | "unverified";

export type NewTenant = {
  name: string;
  slug?: string;
  billing_email?: string;
  owner_email?: string;
  owner_password?: string;
  owner_first_name?: string;
  owner_last_name?: string;
};

export type NewUser = {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  tenant_id?: string | null;
  role: Role;
  is_superuser?: boolean;
  email_verified?: boolean;
};

export type UserUpdate = Partial<{
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  tenant_id: string | null;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  email_verified: boolean;
}>;

export const platformApi = {
  tenants: async (params: { page: number; search?: string; status?: string; page_size?: number }) =>
    (await api.get<Page<AdminTenant>>("/admin/tenants/", { params })).data,
  tenant: async (id: string) =>
    (await api.get<AdminTenantDetail>(`/admin/tenants/${id}/`)).data,
  createTenant: async (data: NewTenant) =>
    (await api.post<AdminTenantDetail>("/admin/tenants/", data)).data,
  updateTenant: async (
    id: string,
    data: Partial<Pick<AdminTenantDetail, "name" | "slug" | "status" | "billing_email" | "timezone" | "locale">>,
  ) => (await api.patch<AdminTenantDetail>(`/admin/tenants/${id}/`, data)).data,
  updateQuota: async (id: string, data: Partial<QuotaLimits>) =>
    (await api.patch<AdminTenantDetail>(`/admin/tenants/${id}/quota/`, data)).data,
  adjustCredit: async (id: string, amount_usd: string, description: string) =>
    (await api.post<AdminTenantDetail>(`/admin/tenants/${id}/credit/`, { amount_usd, description })).data,
  updateSubscription: async (id: string, data: Partial<AdminSubscription>) =>
    (await api.patch<AdminTenantDetail>(`/admin/tenants/${id}/subscription/`, data)).data,
  importTemplates: async (files: File[]) => {
    const form = new FormData();
    files.forEach((f) => form.append("documents", f));
    return (await api.post<{ created: string[]; failed: string[] }>("/admin/assistant-templates/import/", form)).data;
  },
  apiKeys: (tenantId: string): ApiKeysEndpoints => ({
    queryKey: ["admin", "tenant", tenantId, "api-keys"] as const,
    list: async () => (await api.get<ApiKey[]>(`/admin/tenants/${tenantId}/api-keys/`)).data,
    create: async (data: NewApiKey) =>
      (await api.post<ApiKey & { key: string }>(`/admin/tenants/${tenantId}/api-keys/`, data)).data,
    revoke: async (id: string) => {
      await api.post(`/admin/api-keys/${id}/revoke/`);
    },
  }),
  deleteTenant: async (id: string) => {
    await api.delete(`/admin/tenants/${id}/`);
  },

  settings: async () => (await api.get<PlatformSettings>("/admin/settings/")).data,
  updateSettings: async (data: Partial<PlatformSettings>) =>
    (await api.patch<PlatformSettings>("/admin/settings/", data)).data,

  users: async (params: { page: number; search?: string; role?: string; tenant?: string; only?: string }) =>
    (await api.get<Page<AdminUser>>("/admin/users/", { params })).data,
  user: async (id: string) => (await api.get<AdminUserDetail>(`/admin/users/${id}/`)).data,
  createUser: async (data: NewUser) =>
    (await api.post<AdminUserDetail>("/admin/users/", data)).data,
  updateUser: async (id: string, data: UserUpdate) =>
    (await api.patch<AdminUserDetail>(`/admin/users/${id}/`, data)).data,
  setPassword: async (id: string, password: string) =>
    (await api.post<AdminUserDetail>(`/admin/users/${id}/set-password/`, { password })).data,
  unlock: async (id: string) =>
    (await api.post<AdminUserDetail>(`/admin/users/${id}/unlock/`)).data,
  addMembership: async (userId: string, tenant_id: string, role: Role) =>
    (await api.post<AdminUserDetail>(`/admin/users/${userId}/memberships/`, { tenant_id, role })).data,
  updateMembership: async (id: string, data: { role?: Role; is_active?: boolean }) =>
    (await api.patch<AdminUserDetail>(`/admin/memberships/${id}/`, data)).data,
  removeMembership: async (id: string) =>
    (await api.delete<AdminUserDetail>(`/admin/memberships/${id}/`)).data,
};

/** The reason a request failed: the first field error, else the message. */
export function errText(error: unknown, fallback = "Something went wrong."): string {
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  if (!data || typeof data !== "object") return fallback;
  const wrapper = (data as { error?: { message?: string; details?: Record<string, unknown> } }).error;
  const fields = wrapper?.details;
  if (fields && typeof fields === "object") {
    const first = Object.values(fields)[0];
    if (Array.isArray(first) && typeof first[0] === "string") return first[0];
    if (typeof first === "string") return first;
  }
  const message = wrapper?.message ?? (data as { detail?: string }).detail ?? "";
  return message && !message.startsWith("{") && !message.startsWith("[") ? message : fallback;
}

export const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "member", label: "Member" },
  { value: "customer", label: "Customer" },
];

export function fullName(u: { first_name: string; last_name: string; email: string }) {
  return `${u.first_name} ${u.last_name}`.trim() || u.email;
}

export function creditText(millicents: number | null) {
  if (millicents == null) return "—";
  return `$${(millicents / 100000).toFixed(2)}`;
}

export function dateText(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
